export type PriceTrend = 'drop' | 'rise' | 'stable';

export interface Variety {
  id: string;
  name: string;
  nameTa: string;
  minPrice: number;
  maxPrice: number;
  modalPrice: number;
}

export interface Product {
  id: string;
  name: string;
  nameTa: string;
  category: 'vegetables' | 'fruits' | 'dry-fruits' | 'greens' | 'spices';
  image: string;
  imageAlt: string;
  varieties: Variety[];
  currentPrice: number;
  previousPrice: number;
  priceChange: number;
  priceChangePct: number;
  trend: PriceTrend;
  unit: string;
  priceHistory: number[];
  watchlistCount: number;
  isWatched: boolean;
  isFeatured: boolean;
  isSeasonal: boolean;
  seasonNote?: string;
  seasonNoteTa?: string;
  lastUpdated: string;
}

export interface Category {
  id: string;
  name: string;
  nameTa: string;
  icon: string;
  count: number;
  color: string;
}

export const CATEGORIES: Category[] = [
{ id: 'all', name: 'All', nameTa: 'அனைத்தும்', icon: '🌿', count: 45, color: 'bg-secondary text-secondary-foreground' },
{ id: 'vegetables', name: 'Vegetables', nameTa: 'காய்கறிகள்', icon: '🥦', count: 22, color: 'bg-green-light text-primary' },
{ id: 'fruits', name: 'Fruits', nameTa: 'பழங்கள்', icon: '🍊', count: 8, color: 'bg-amber-light text-accent-foreground' },
{ id: 'dry-fruits', name: 'Dry Fruits', nameTa: 'உலர் பழங்கள்', icon: '🥜', count: 6, color: 'bg-amber-light text-accent-foreground' },
{ id: 'greens', name: 'Greens', nameTa: 'கீரைகள்', icon: '🌱', count: 5, color: 'bg-green-light text-primary' },
{ id: 'spices', name: 'Spices', nameTa: 'மசாலாகள்', icon: '🌶️', count: 4, color: 'bg-red-light text-rise-red' }];


export const PRODUCTS: Product[] = [
{
  id: 'prod-tomato',
  name: 'Tomato',
  nameTa: 'தக்காளி',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1694879637374-46c26a200fab",
  imageAlt: 'Fresh red tomatoes cluster on vine, bright red, market fresh',
  varieties: [
  { id: 'var-tom-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 28, maxPrice: 42, modalPrice: 35 },
  { id: 'var-tom-2', name: 'Hybrid', nameTa: 'கலப்பின', minPrice: 32, maxPrice: 48, modalPrice: 40 }],

  currentPrice: 35, previousPrice: 48, priceChange: -13, priceChangePct: -27.1,
  trend: 'drop', unit: 'kg',
  priceHistory: [62, 58, 55, 52, 48, 42, 35],
  watchlistCount: 3842, isWatched: true, isFeatured: true, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-onion-big',
  name: 'Onion (Big)',
  nameTa: 'பெரிய வெங்காயம்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1571070970616-b7e02d1e0d8c",
  imageAlt: 'Brown onions piled in market, dry outer skin, round shape',
  varieties: [
  { id: 'var-onb-1', name: 'Nashik', nameTa: 'நாசிக்', minPrice: 22, maxPrice: 32, modalPrice: 26 },
  { id: 'var-onb-2', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 18, maxPrice: 28, modalPrice: 22 }],

  currentPrice: 26, previousPrice: 24, priceChange: 2, priceChangePct: 8.3,
  trend: 'rise', unit: 'kg',
  priceHistory: [20, 21, 22, 23, 24, 25, 26],
  watchlistCount: 2914, isWatched: false, isFeatured: true, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-onion-small',
  name: 'Onion (Small)',
  nameTa: 'சின்ன வெங்காயம்',
  category: 'vegetables',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_140042fc9-1773092222364.png",
  imageAlt: 'Small shallot onions in a basket, purple-pink skin, market fresh',
  varieties: [
  { id: 'var-ons-1', name: 'Shallot', nameTa: 'சிறிய வெங்காயம்', minPrice: 45, maxPrice: 62, modalPrice: 52 }],

  currentPrice: 52, previousPrice: 42, priceChange: 10, priceChangePct: 23.8,
  trend: 'rise', unit: 'kg',
  priceHistory: [38, 40, 40, 41, 42, 46, 52],
  watchlistCount: 1823, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-potato',
  name: 'Potato',
  nameTa: 'உருளைக்கிழங்கு',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1574042025859-9da475a15832",
  imageAlt: 'Fresh brown potatoes in a pile, earthy skin, market produce',
  varieties: [
  { id: 'var-pot-1', name: 'Jyoti', nameTa: 'ஜோதி', minPrice: 22, maxPrice: 32, modalPrice: 27 },
  { id: 'var-pot-2', name: 'Kufri', nameTa: 'குஃப்ரி', minPrice: 24, maxPrice: 34, modalPrice: 29 }],

  currentPrice: 27, previousPrice: 23, priceChange: 4, priceChangePct: 17.4,
  trend: 'rise', unit: 'kg',
  priceHistory: [22, 22, 23, 23, 23, 25, 27],
  watchlistCount: 1245, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-green-chilli',
  name: 'Green Chilli',
  nameTa: 'பச்சை மிளகாய்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1712057229152-bd9edfb0fb9d",
  imageAlt: 'Bright green chillies in a cluster, fresh and glossy, market produce',
  varieties: [
  { id: 'var-gc-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 50, maxPrice: 68, modalPrice: 58 }],

  currentPrice: 58, previousPrice: 72, priceChange: -14, priceChangePct: -19.4,
  trend: 'drop', unit: 'kg',
  priceHistory: [80, 78, 75, 72, 68, 64, 58],
  watchlistCount: 987, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-carrot',
  name: 'Carrot',
  nameTa: 'கேரட்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1689011117499-4afd9a55dc81",
  imageAlt: 'Orange carrots with green tops, fresh from Ooty hills, market produce',
  varieties: [
  { id: 'var-car-1', name: 'Ooty', nameTa: 'ஊட்டி', minPrice: 55, maxPrice: 72, modalPrice: 62 },
  { id: 'var-car-2', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 45, maxPrice: 58, modalPrice: 50 }],

  currentPrice: 62, previousPrice: 57, priceChange: 5, priceChangePct: 8.8,
  trend: 'rise', unit: 'kg',
  priceHistory: [50, 52, 54, 55, 57, 60, 62],
  watchlistCount: 756, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-beans',
  name: 'Beans',
  nameTa: 'பீன்ஸ்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1657430337803-f650c87c631d",
  imageAlt: 'Fresh green beans in a bundle, bright green, market produce',
  varieties: [
  { id: 'var-bns-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 65, maxPrice: 90, modalPrice: 77 }],

  currentPrice: 77, previousPrice: 67, priceChange: 10, priceChangePct: 14.9,
  trend: 'rise', unit: 'kg',
  priceHistory: [58, 60, 62, 64, 67, 72, 77],
  watchlistCount: 634, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-cucumber',
  name: 'Cucumber',
  nameTa: 'வெள்ளரிக்காய்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1693933858151-2d01597907e1",
  imageAlt: 'Fresh green cucumbers on wooden surface, cool and refreshing',
  varieties: [
  { id: 'var-cuc-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 22, maxPrice: 34, modalPrice: 27 }],

  currentPrice: 27, previousPrice: 35, priceChange: -8, priceChangePct: -22.9,
  trend: 'drop', unit: 'kg',
  priceHistory: [40, 38, 36, 35, 32, 30, 27],
  watchlistCount: 2156, isWatched: true, isFeatured: true, isSeasonal: true,
  seasonNote: 'Perfect for summer hydration',
  seasonNoteTa: 'கோடை வெப்பத்தில் சிறந்தது',
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-broad-beans',
  name: 'Broad Beans',
  nameTa: 'அவரைக்காய்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1615477538262-f791f21e75d6",
  imageAlt: 'Fresh broad beans in a pile, flat green pods, market produce',
  varieties: [
  { id: 'var-bb-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 35, maxPrice: 55, modalPrice: 44 }],

  currentPrice: 44, previousPrice: 74, priceChange: -30, priceChangePct: -40.5,
  trend: 'drop', unit: 'kg',
  priceHistory: [90, 85, 80, 74, 65, 55, 44],
  watchlistCount: 445, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-garlic',
  name: 'Garlic',
  nameTa: 'பூண்டு',
  category: 'spices',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17baf9b36-1773092221417.png",
  imageAlt: 'Fresh white garlic bulbs in a cluster, papery skin, market produce',
  varieties: [
  { id: 'var-gar-1', name: 'Indian', nameTa: 'இந்திய', minPrice: 110, maxPrice: 145, modalPrice: 126 },
  { id: 'var-gar-2', name: 'Chinese', nameTa: 'சீன', minPrice: 90, maxPrice: 120, modalPrice: 104 }],

  currentPrice: 126, previousPrice: 118, priceChange: 8, priceChangePct: 6.8,
  trend: 'rise', unit: 'kg',
  priceHistory: [108, 110, 112, 115, 118, 122, 126],
  watchlistCount: 1534, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-ginger',
  name: 'Ginger',
  nameTa: 'இஞ்சி',
  category: 'spices',
  image: "https://images.unsplash.com/photo-1507311687154-46df8bbb65dd",
  imageAlt: 'Fresh ginger root pieces on wooden surface, knobby tan skin',
  varieties: [
  { id: 'var-gin-1', name: 'Local Fresh', nameTa: 'உள்ளூர் புதிய', minPrice: 72, maxPrice: 98, modalPrice: 84 }],

  currentPrice: 84, previousPrice: 94, priceChange: -10, priceChangePct: -10.6,
  trend: 'drop', unit: 'kg',
  priceHistory: [100, 98, 96, 94, 92, 88, 84],
  watchlistCount: 892, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-drumstick',
  name: 'Drumstick',
  nameTa: 'முருங்கைக்காய்',
  category: 'vegetables',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a09f54cb-1774272814036.png",
  imageAlt: 'Long green drumstick pods in a bundle, Moringa, South Indian vegetable',
  varieties: [
  { id: 'var-drm-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 65, maxPrice: 95, modalPrice: 80 }],

  currentPrice: 80, previousPrice: 80, priceChange: 0, priceChangePct: 0,
  trend: 'stable', unit: 'kg',
  priceHistory: [75, 78, 80, 80, 80, 80, 80],
  watchlistCount: 678, isWatched: false, isFeatured: false, isSeasonal: true,
  seasonNote: 'Peak season — high availability',
  seasonNoteTa: 'உச்ச பருவம் — அதிக கிடைக்கும்',
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-mango-raw',
  name: 'Raw Mango',
  nameTa: 'மாங்காய்',
  category: 'fruits',
  image: "https://images.unsplash.com/photo-1625382234122-56dde3655bf4",
  imageAlt: 'Green raw mangoes hanging on tree, unripe, summer produce',
  varieties: [
  { id: 'var-mrm-1', name: 'Kili Mooku', nameTa: 'கிளிமூக்கு', minPrice: 10, maxPrice: 20, modalPrice: 14 },
  { id: 'var-mrm-2', name: 'Neelam', nameTa: 'நீலம்', minPrice: 12, maxPrice: 22, modalPrice: 16 }],

  currentPrice: 14, previousPrice: 22, priceChange: -8, priceChangePct: -36.4,
  trend: 'drop', unit: 'kg',
  priceHistory: [35, 30, 26, 22, 20, 17, 14],
  watchlistCount: 1876, isWatched: true, isFeatured: true, isSeasonal: true,
  seasonNote: 'Mango season peak — best time to buy',
  seasonNoteTa: 'மாம்பழ சீசன் — வாங்க சிறந்த நேரம்',
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-banana',
  name: 'Banana',
  nameTa: 'வாழைப்பழம்',
  category: 'fruits',
  image: "https://images.unsplash.com/photo-1721400500658-73222904fc5d",
  imageAlt: 'Fresh yellow bananas in a bunch, ripe and market fresh',
  varieties: [
  { id: 'var-ban-1', name: 'Poovan', nameTa: 'பூவன்', minPrice: 28, maxPrice: 38, modalPrice: 32 },
  { id: 'var-ban-2', name: 'Nendran', nameTa: 'நேந்திரம்', minPrice: 45, maxPrice: 65, modalPrice: 54 }],

  currentPrice: 32, previousPrice: 30, priceChange: 2, priceChangePct: 6.7,
  trend: 'rise', unit: 'kg',
  priceHistory: [28, 28, 29, 30, 30, 31, 32],
  watchlistCount: 1102, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-spinach',
  name: 'Spinach',
  nameTa: 'பசலைக்கீரை',
  category: 'greens',
  image: "https://images.unsplash.com/photo-1683536905403-ea18a3176d29",
  imageAlt: 'Fresh green spinach leaves in a bundle, bright green, market produce',
  varieties: [
  { id: 'var-spi-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 10, maxPrice: 18, modalPrice: 14 }],

  currentPrice: 14, previousPrice: 16, priceChange: -2, priceChangePct: -12.5,
  trend: 'drop', unit: 'kg',
  priceHistory: [20, 18, 18, 16, 16, 15, 14],
  watchlistCount: 432, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-coriander',
  name: 'Coriander Leaves',
  nameTa: 'கொத்துமல்லி',
  category: 'greens',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1188febf7-1772955333597.png",
  imageAlt: 'Fresh coriander leaves in a bunch, bright green, aromatic herb',
  varieties: [
  { id: 'var-cor-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 10, maxPrice: 20, modalPrice: 15 }],

  currentPrice: 15, previousPrice: 18, priceChange: -3, priceChangePct: -16.7,
  trend: 'drop', unit: 'kg',
  priceHistory: [24, 22, 20, 18, 17, 16, 15],
  watchlistCount: 567, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-cashew',
  name: 'Cashew',
  nameTa: 'முந்திரி',
  category: 'dry-fruits',
  image: "https://images.unsplash.com/photo-1695492604882-98ee68f2a475",
  imageAlt: 'Whole cashew nuts in a pile, cream colored, premium dry fruit',
  varieties: [
  { id: 'var-cas-1', name: 'W240', nameTa: 'W240', minPrice: 780, maxPrice: 860, modalPrice: 820 },
  { id: 'var-cas-2', name: 'W320', nameTa: 'W320', minPrice: 680, maxPrice: 760, modalPrice: 720 }],

  currentPrice: 820, previousPrice: 840, priceChange: -20, priceChangePct: -2.4,
  trend: 'drop', unit: 'kg',
  priceHistory: [860, 855, 848, 840, 835, 828, 820],
  watchlistCount: 2341, isWatched: true, isFeatured: true, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-almond',
  name: 'Almond',
  nameTa: 'பாதாம்',
  category: 'dry-fruits',
  image: "https://images.unsplash.com/photo-1707586764292-ab9cae055cbd",
  imageAlt: 'Raw almonds in a pile, oval shaped, light brown skin, premium dry fruit',
  varieties: [
  { id: 'var-alm-1', name: 'California', nameTa: 'கலிஃபோர்னியா', minPrice: 760, maxPrice: 840, modalPrice: 800 }],

  currentPrice: 800, previousPrice: 790, priceChange: 10, priceChangePct: 1.3,
  trend: 'rise', unit: 'kg',
  priceHistory: [780, 782, 785, 788, 790, 795, 800],
  watchlistCount: 1678, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-beetroot',
  name: 'Beetroot',
  nameTa: 'பீட்ரூட்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1527790806964-dfa3c2c7e032",
  imageAlt: 'Deep purple beetroots with green tops, fresh market produce',
  varieties: [
  { id: 'var-bet-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 32, maxPrice: 48, modalPrice: 39 }],

  currentPrice: 39, previousPrice: 49, priceChange: -10, priceChangePct: -20.4,
  trend: 'drop', unit: 'kg',
  priceHistory: [56, 54, 52, 49, 46, 42, 39],
  watchlistCount: 523, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-cabbage',
  name: 'Cabbage',
  nameTa: 'முட்டைக்கோஸ்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1665525928337-7e5b116bf82a",
  imageAlt: 'Fresh green cabbage head, tightly packed leaves, market produce',
  varieties: [
  { id: 'var-cab-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 28, maxPrice: 45, modalPrice: 36 }],

  currentPrice: 36, previousPrice: 36, priceChange: 0, priceChangePct: 0,
  trend: 'stable', unit: 'kg',
  priceHistory: [38, 37, 36, 36, 36, 36, 36],
  watchlistCount: 312, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
},
{
  id: 'prod-ladies-finger',
  name: 'Ladies Finger',
  nameTa: 'வெண்டைக்காய்',
  category: 'vegetables',
  image: "https://images.unsplash.com/photo-1558408525-1092038389ae",
  imageAlt: 'Fresh green okra pods in a pile, ridged, market produce',
  varieties: [
  { id: 'var-lf-1', name: 'Local', nameTa: 'உள்ளூர்', minPrice: 35, maxPrice: 52, modalPrice: 42 }],

  currentPrice: 42, previousPrice: 42, priceChange: 0, priceChangePct: 0,
  trend: 'stable', unit: 'kg',
  priceHistory: [40, 42, 42, 42, 42, 42, 42],
  watchlistCount: 445, isWatched: false, isFeatured: false, isSeasonal: false,
  lastUpdated: '06:30 AM'
}];


export const MARKET_STATS = {
  totalProducts: 45,
  updatedToday: 42,
  biggestDrop: { product: 'Broad Beans', change: -40.5, price: 44 },
  biggestRise: { product: 'Onion (Small)', change: 23.8, price: 52 },
  lastUpdated: '06:30 AM, 19 Jun 2025',
  marketStatus: 'open\' as \'open\' | \'closed',
  weatherToday: 'Sunny & Hot, 36°C',
  weatherTodayTa: 'வெயில் & வெப்பம், 36°C'
};

export const TOP_DROPS = PRODUCTS.
filter((p) => p.trend === 'drop').
sort((a, b) => a.priceChangePct - b.priceChangePct).
slice(0, 5);

export const TOP_RISES = PRODUCTS.
filter((p) => p.trend === 'rise').
sort((a, b) => b.priceChangePct - a.priceChangePct).
slice(0, 5);

export const SEASONAL_PICKS = PRODUCTS.filter((p) => p.isSeasonal);

export const TRENDING = PRODUCTS.
sort((a, b) => b.watchlistCount - a.watchlistCount).
slice(0, 6);

export const WATCHLIST_PRODUCTS = PRODUCTS.filter((p) => p.isWatched);

export interface AlertRule {
  id: string;
  productId: string;
  productName: string;
  productNameTa: string;
  condition: 'below' | 'above';
  threshold: number;
  isActive: boolean;
  lastTriggered?: string;
}

export const ALERT_RULES: AlertRule[] = [
{ id: 'alert-001', productId: 'prod-tomato', productName: 'Tomato', productNameTa: 'தக்காளி', condition: 'below', threshold: 20, isActive: true },
{ id: 'alert-002', productId: 'prod-onion-big', productName: 'Onion (Big)', productNameTa: 'பெரிய வெங்காயம்', condition: 'below', threshold: 25, isActive: true },
{ id: 'alert-003', productId: 'prod-cashew', productName: 'Cashew', productNameTa: 'முந்திரி', condition: 'below', threshold: 800, isActive: true, lastTriggered: '15 Jun 2025' },
{ id: 'alert-004', productId: 'prod-mango-raw', productName: 'Raw Mango', productNameTa: 'மாங்காய்', condition: 'below', threshold: 10, isActive: false }];